import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '../../../store/store';
import { JWTHeader } from '../../../utils/JWTHeader';
import { showToast } from '../Notification';

interface PreviewViewerProps {
  date: string;
  slug: string;
  pageCount: number;
  templateName: string;
  className?: string;
  showNavigation?: boolean;
  currentPage?: number;
  onPageChange?: (page: number) => void;
}

function PreviewViewer({
  date,
  slug,
  pageCount,
  className = '',
  showNavigation = true,
  currentPage: controlledCurrentPage,
  onPageChange,
  templateName,
}: PreviewViewerProps) {
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [currentPage, setCurrentPage] = useState(controlledCurrentPage || 1);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update currentPage when controlled prop changes
  useEffect(() => {
    if (controlledCurrentPage !== undefined) {
      setCurrentPage(controlledCurrentPage);
    }
  }, [controlledCurrentPage]);

  // Load preview image when page changes
  useEffect(() => {
    if (!jwt || pageCount === 0) {
      return;
    }

    const loadPreview = async() => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `/api/fixed-newsletters/${date}/${slug}/templates/${templateName}/preview/${currentPage}`,
          JWTHeader(jwt),
        );

        if (!response.ok) {
          throw new Error('Failed to load preview');
        }

        // Create blob URL for the image
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        
        // Clean up previous URL
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }
        
        setPreviewUrl(url);
      } catch (err) {
        console.error('Error loading preview:', err);
        setError('Failed to load preview');
        showToast('error', 'Erreur lors du chargement de l\'aperçu');
      } finally {
        setIsLoading(false);
      }
    };

    loadPreview();

    // Cleanup function
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [jwt, date, slug, currentPage, pageCount, templateName]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      if (onPageChange) {
        onPageChange(newPage);
      } else {
        setCurrentPage(newPage);
      }
    }
  };

  const handleNextPage = () => {
    if (currentPage < pageCount) {
      const newPage = currentPage + 1;
      if (onPageChange) {
        onPageChange(newPage);
      } else {
        setCurrentPage(newPage);
      }
    }
  };

  if (pageCount === 0) {
    return (
      <div className={`flex items-center justify-center rounded bg-gray-100 ${className}`}>
        <span className="px-4 text-center text-gray-500">
          Aucun aperçu disponible
        </span>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {/* Preview Image */}
      <div className="relative h-full w-full overflow-hidden rounded bg-gray-50">
        {isLoading ? (
          <div className="flex h-full w-full items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
          </div>
        ) : error ? (
          <div className="flex h-full w-full items-center justify-center">
            <span className="px-4 text-center text-sm text-red-500">{error}</span>
          </div>
        ) : previewUrl ? (
          <img
            src={previewUrl}
            alt={`Preview page ${currentPage}`}
            className="h-full w-full object-contain"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <span className="px-4 text-center text-gray-500">
              Aperçu non disponible
            </span>
          </div>
        )}

        {/* Navigation Arrows */}
        {showNavigation && pageCount > 1 && !isLoading && !error && (
          <>
            {/* Previous Page Arrow */}
            <button
              onClick={handlePreviousPage}
              disabled={currentPage <= 1}
              className={`bg-opacity-50 hover:bg-opacity-70 absolute top-1/2 left-2 z-10 -translate-y-1/2 rounded-full bg-black p-2 text-white transition-all duration-200 ${
                currentPage <= 1 ? 'cursor-not-allowed opacity-50' : 'opacity-80 hover:opacity-100'
              }`}
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                position: 'absolute',
                zIndex: 10,
              }}
              aria-label="Page précédente"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>

            {/* Next Page Arrow */}
            <button
              onClick={handleNextPage}
              disabled={currentPage >= pageCount}
              className={`bg-opacity-50 hover:bg-opacity-70 absolute top-1/2 right-2 z-10 -translate-y-1/2 rounded-full bg-black p-2 text-white transition-all duration-200 ${
                currentPage >= pageCount ? 'cursor-not-allowed opacity-50' : 'opacity-80 hover:opacity-100'
              }`}
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                position: 'absolute',
                zIndex: 10,
              }}
              aria-label="Page suivante"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </>
        )}

        {/* Page Indicator */}
        {pageCount > 1 && !isLoading && !error && (
          <div 
            className="bg-opacity-50 absolute bottom-2 left-1/2 z-10 -translate-x-1/2 rounded bg-black px-2 py-1 text-xs text-white"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              position: 'absolute',
              zIndex: 10,
            }}
          >
            {currentPage} / {pageCount}
          </div>
        )}
      </div>
    </div>
  );
}

export default PreviewViewer; 